"use client";

import { useEffect, useState } from "react";

import { css } from "@/../styled-system/css";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/Carousel";

import NewProductCard from "../Common/NewProductCard";

interface PopularProductCarouselProps {
  products: {
    name: string;
    price: number;
    image_url: string;
    slug: string;
  }[];
}

function PopularProductCarousel({
  products: randomProducts,
}: PopularProductCarouselProps) {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const updateScrollState = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    updateScrollState();
    api.on("select", updateScrollState);
    api.on("reInit", updateScrollState);

    return () => {
      api.off("select", updateScrollState);
      api.off("reInit", updateScrollState);
    };
  }, [api]);

  if (!randomProducts || randomProducts.length === 0) {
    return null;
  }

  const products = Array.from(
    { length: Math.min(6, randomProducts.length) },
    (_, i) => ({
      id: i + 1,
      name: randomProducts[i].name,
      price: randomProducts[i].price,
      image_url: randomProducts[i].image_url,
      slug: randomProducts[i].slug,
    }),
  );

  return (
    <div className={css({ position: "relative", w: "full" })}>
      <Carousel setApi={setApi} opts={{ dragFree: true }}>
        <CarouselContent
          css={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8",
            pb: "2",
          }}
        >
          {products.map((product) => (
            <CarouselItem key={product.id} css={{ flexBasis: "200px" }}>
              <NewProductCard {...product} />
            </CarouselItem>
          ))}
        </CarouselContent>

        {canScrollPrev && (
          <CarouselPrevious
            css={{
              top: "calc(50% - 4px)",
              left: "2",
            }}
          />
        )}
        {canScrollNext && (
          <CarouselNext
            css={{
              top: "calc(50% - 4px)",
              right: "2",
            }}
          />
        )}
      </Carousel>
    </div>
  );
}

export default PopularProductCarousel;
